import { ModelBase } from '../../core/model/model-base';
import { Property } from '../../rent/model/property';
import { ErrandStatus, ErrandStatusToString, ErrandType, ErrandTypeToString } from './const/enums';
import { ExecutorGroup } from './executor-group';
import { WebUser } from '../../common/model/webUser';
import { Building } from '../../rent/model/building';
import { ErrandTagSelection } from '../../standalone-errand/model/errand-tag';

export class Errand extends ModelBase {
  constructor(dataContext?: Errand) {
    super();
    if (dataContext) {
      this.errandNumber = dataContext.errandNumber;
      this.name = dataContext.name;
      this.description = dataContext.description;
      this.status = dataContext.status;
      this.type = dataContext.type;
      this.property = dataContext.property;
      this.webUser = dataContext.webUser;
      this.executorGroup = dataContext.executorGroup;
      this.resource = dataContext.resource;
      this.tag = dataContext.tag;
      this.dwelling = dataContext.dwelling;
      this.unreadDialogMessagesCount = dataContext.unreadDialogMessagesCount ?? 0;
      this.buildings = dataContext.buildings?.map(x => new Building(x)) || [];
      this.checklists = dataContext.checklists?.map(x => new ErrandChecklist(x)) || [];
    }
  }

  errandNumber: string;
  name: string;
  dwelling: string;
  description: string;
  status: ErrandStatus;
  type: ErrandType;
  property: Property;
  buildings: Building[];
  webUser: WebUser;
  executorGroup: ExecutorGroup;
  resource: WebUser;
  tag: ErrandTagSelection;
  checklists: ErrandChecklist[];
  unreadDialogMessagesCount: number = 0;

  get errandStatus() {
    return this.status ? ErrandStatusToString(this.status) : '';
  }

  get errandType() {
    return this.type ? ErrandTypeToString(this.type) : '';
  }

  get errandWebUserName() {
    return this.webUser?.personName || this.webUser?.name || '';
  }

  get errandExecutorGroupName() {
    return this.executorGroup?.name || '';
  }

  get errandResourceName() {
    return this.resource?.personName || this.resource?.name || '';
  }

  get errandTagName() {
    return this.tag?.name || '';
  }

  get propertyName() {
    if (!this.property) {
      return '';
    }

    return this.property.propertyNumber
      ? `${this.property.propertyNumber} ${this.property.propertyName}`
      : this.property.propertyName || '';
  }

  get companyName() {
    return this.property?.companyName || '';
  }
  
  get totalChecklistRowsCount(): number {
    return this.checklists?.length;
  }

  get completedChecklistRowsCount(): number {
    return this.checklists?.filter(cl => cl.isCompleted)?.length;
  }

  get checklistRowsProgressText() {
    if (this.totalChecklistRowsCount && this.completedChecklistRowsCount != null) {
      return `${this.completedChecklistRowsCount} / ${this.totalChecklistRowsCount}`;
    }

    return '';
  }

  get buildingNames(): string {
    return this.buildings?.map(x => `${x.number} ${x.name}`).join(',');
  }

  get checklistsNames(): string[] {
    return this.checklists?.map(cl => cl.name);
  }

  get checklistsCount(): number {
    return this.checklists?.length;
  }

  toString(): string {
    return this.name;
  }
}

export class ErrandChecklist extends ModelBase {
  constructor(dataContext?: ErrandChecklist) {
      super();
      if (dataContext) {
        this.id = dataContext.id;
        this.name = dataContext.name;
        this.note = dataContext.note;
        this.isCompleted = dataContext.isCompleted;
        this.completedTime = dataContext.completedTime;
        this.completedByWebUser = dataContext.completedByWebUser;
        this.checklistRows = dataContext.checklistRows.map(x => new ErrandChecklistRow(x));
      }
    }
  
    name: string;
    note: string;
    isCompleted: boolean;
    completedTime?: Date;
    completedByWebUser?: WebUser;
    checklistRows: ErrandChecklistRow[];
}

export class ErrandChecklistRow extends ModelBase {
  constructor(dataContext?: ErrandChecklistRow) {
    super();
    if (dataContext) {
      this.id = dataContext.id;
      this.text = dataContext.text;
      this.order = dataContext.order;
      this.isCompleted = dataContext.isCompleted;
      this.completedTime = dataContext.completedTime;
      this.completedByWebUser = dataContext.completedByWebUser;
    }
  }

  text: string;
  order: number;
  isCompleted: boolean;
  completedTime?: Date;
  completedByWebUser?: WebUser;
  note: string;
}
